/**
 * The workflow editor (ADR-0009, FR-C3) — a first-class staff screen, because
 * a firm-configurable workflow that can only be configured by an engineer is
 * not firm-configurable.
 *
 * Three things about it are decisions rather than layout:
 *
 * 1. **Validation is the server's.** The Check button posts the unsaved stages
 *    and renders exactly what came back. There is no second copy of the rules
 *    in TypeScript — two validators that agree today disagree in a year, and
 *    the one the firm sees would be the wrong one.
 * 2. **A version cases are running on is read-only**, and the banner says so
 *    with the remedy rather than with the word "conflict". Making a new
 *    version is one button away and copies everything.
 * 3. **A firm never sees the word "key".** Stage keys are derived from names,
 *    here and on the server by the same rule, so that `on_approve` can point
 *    at a stage that survives a rename.
 *
 * **Wave 15F** (C14 §10.6 item 1, ADR-0032 §5): an **edit page**. `EditPage`
 * carries the title; `EditBar` is the one Save, with *Unsaved changes*, a
 * Discard, and the view-only line for a version that cannot change; leaving
 * with unsaved stages asks first (`useLeaveGuard`). Check, Publish and *Start
 * a new version* sit at the top right — never a second Save. Publish is held
 * while there are unsaved changes, and says why: it publishes what is
 * **saved**, and the old screen reloaded over the unsaved stages without a
 * word. The file is split along its parts: `StageCard`, `Versions`,
 * `IssueList` and the pure rules in `draft.ts`.
 */

import { Alert, Badge, Button } from '@fiveox/ui'
import { Check, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { RecordPage } from '../../components/page'
import { SERVICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type Issue,
  type StageDraft,
  type Validation,
  type VersionSummary,
  type Vocabulary,
  type WorkflowSummary,
  blankStage,
  editability,
  groupIssues,
  reorder,
  services,
  toDrafts,
} from '../../lib/api/services'
import { sameStages, validationSummary } from './draft'
import { IssueList } from './IssueList'
import { StageCard } from './StageCard'
import { VERSION_TONE, Versions } from './Versions'

export function WorkflowEditor() {
  const { serviceId = '' } = useParams()
  const { can } = useEvaluation()
  const mayEdit = can(SERVICE, 'can_update')

  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null)
  const [version, setVersion] = useState<VersionSummary | null>(null)
  const [stages, setStages] = useState<StageDraft[]>([])
  /** What the server holds for the open version — Discard's target, and what
   *  *dirty* is measured against. */
  const [savedStages, setSavedStages] = useState<StageDraft[]>([])
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [validation, setValidation] = useState<Validation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  const openVersion = useCallback(async (versionId: string) => {
    const body = await services.version(versionId)
    setVersion(body.data)
    setStages(toDrafts(body.data.stages))
    setSavedStages(toDrafts(body.data.stages))
    setValidation(body.data.validation)
  }, [])

  const load = useCallback(async () => {
    const [wf, vocab] = await Promise.all([services.workflow(serviceId), services.vocabulary()])
    setWorkflow(wf.data)
    setVocabulary(vocab.data)
    // Open the draft if there is one — a firm that started a new version and
    // came back should land where they left off, not on the live one.
    const draft = wf.data.versions.find((v) => v.status === 'draft')
    const target = draft?.id ?? wf.data.published_version_id
    if (target) await openVersion(target)
  }, [serviceId, openVersion])

  useEffect(() => {
    void load().catch(() => setError('That workflow could not be loaded.'))
  }, [load])

  const { editable, reason } = editability(version)
  const live = mayEdit && editable
  const dirty = live && !sameStages(stages, savedStages)
  const guard = useLeaveGuard(dirty)

  const errorsByStage = useMemo(() => groupIssues(validation?.errors ?? []), [validation])
  const warningsByStage = useMemo(() => groupIssues(validation?.warnings ?? []), [validation])

  const moverRoles = vocabulary?.roles.filter((r) => r.may_move_a_case) ?? []

  const update = (index: number, changes: Partial<StageDraft>) => {
    setStages(stages.map((stage, i) => (i === index ? { ...stage, ...changes } : stage)))
    setNotice(null)
  }

  /** A refusal that carries the issue list lands on the cards that caused it. */
  const takeRefusal = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && Array.isArray(err.details.errors)) {
      setValidation({
        ok: false,
        errors: err.details.errors as Issue[],
        warnings: (err.details.warnings ?? []) as Issue[],
      })
    }
    setError(err instanceof ApiError ? err.message : fallback)
  }

  const check = async () => {
    if (!version) return
    setBusy(true)
    setError(null)
    try {
      const body = await services.check(version.id, stages)
      setValidation(body.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The check did not run.')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!version) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const body = await services.saveStages(version.id, stages)
      setVersion(body.data)
      setStages(toDrafts(body.data.stages))
      setSavedStages(toDrafts(body.data.stages))
      setValidation(body.data.validation)
    } catch (err) {
      takeRefusal(err, 'That workflow could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const publish = async () => {
    if (!version) return
    setBusy(true)
    setError(null)
    try {
      await services.publish(version.id)
      await load()
      setNotice(`Version ${version.version} is published. New cases on this service follow it.`)
    } catch (err) {
      takeRefusal(err, 'That version could not be published.')
    } finally {
      setBusy(false)
    }
  }

  const newVersion = async () => {
    setBusy(true)
    setError(null)
    try {
      const body = await services.newVersion(serviceId, { copy_from: version?.id })
      await load()
      await openVersion(body.data.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'A new version could not be started.')
    } finally {
      setBusy(false)
    }
  }

  const back = { to: `/services/${serviceId}`, label: workflow?.service?.name ?? 'Service' }

  if (!workflow || !version) {
    return <RecordPage back={back} title="Workflow" loading error={error} />
  }

  const stageKeys = stages.map((s) => s.key ?? '')
  const summary = validationSummary(validation)
  const nextVersion = Math.max(...workflow.versions.map((v) => v.version)) + 1

  return (
    <EditPage
      back={back}
      title="Workflow"
      description={`How a case on ${workflow.service?.name ?? 'this service'} moves — its stages, who may move it, what the client is told, and what happens by itself.`}
      meta={
        <>
          <Badge tone={VERSION_TONE[version.status]} size="md">
            Version {version.version} · {version.status}
          </Badge>
          {version.in_use && (
            <Badge tone="info" size="md">
              Cases are running on it
            </Badge>
          )}
          {summary && (
            <Badge tone={summary.tone} size="md">
              {summary.text}
            </Badge>
          )}
        </>
      }
      actions={
        <>
          <Button variant="outline" onClick={() => void check()} disabled={busy || saving}>
            <Check size={16} aria-hidden /> Check
          </Button>
          {live && version.status !== 'published' && (
            <Button
              variant="outline"
              onClick={() => void publish()}
              disabled={busy || saving || dirty}
              title={dirty ? 'Save your changes first — publishing sends what is saved.' : undefined}
            >
              Publish version {version.version}
            </Button>
          )}
          {mayEdit && !editable && (
            <Button variant="outline" onClick={() => void newVersion()} disabled={busy}>
              Start a new version
            </Button>
          )}
        </>
      }
    >
      {guard}

      {error && <Alert tone="error">{error}</Alert>}
      {notice && !error && <Alert tone="success">{notice}</Alert>}

      {!editable && reason && (
        <Alert tone="info">
          {reason}
          {mayEdit && (
            <Button variant="link" className="ml-2" onClick={() => void newVersion()} disabled={busy}>
              Start version {nextVersion}
            </Button>
          )}
        </Alert>
      )}

      {vocabulary && !vocabulary.review_gate_enabled && (
        <Alert tone="warning">
          Your firm's review gate is switched off in Settings. Review stages here keep their settings
          but will not hold a case until you turn it back on.
        </Alert>
      )}

      <IssueList issues={errorsByStage.get(null) ?? []} tone="error" />
      <IssueList issues={warningsByStage.get(null) ?? []} tone="warning" />

      {stages.map((stage, index) => (
        <StageCard
          key={index}
          index={index}
          stage={stage}
          stages={stages}
          live={live}
          moverRoles={moverRoles}
          actionKinds={vocabulary?.action_kinds ?? []}
          errors={errorsByStage.get(stage.key ?? '') ?? []}
          warnings={warningsByStage.get(stage.key ?? '') ?? []}
          onChange={(changes) => update(index, changes)}
          onMove={(to) => setStages(reorder(stages, index, to))}
          onRemove={() => setStages(stages.filter((_, i) => i !== index))}
        />
      ))}

      {live && (
        <Button
          variant="outline"
          onClick={() => setStages([...stages, blankStage(stageKeys, moverRoles.map((r) => r.code))])}
        >
          <Plus size={16} aria-hidden /> Add a stage
        </Button>
      )}

      <Versions versions={workflow.versions} openId={version.id} onOpen={(id) => void openVersion(id)} />

      <EditBar
        dirty={dirty}
        saving={saving}
        onSave={() => void save()}
        onDiscard={() => {
          setStages(savedStages)
          setError(null)
        }}
        editable={live}
      />
    </EditPage>
  )
}
